import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

type EditFieldTemplateDialogProps = {
  open: boolean;
  templateId: string | null;
  name: string;
  fieldJson: string;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onChangeName: (value: string) => void;
  onChangeFieldJson: (value: string) => void;
  onConfirmSave: () => void;
};

export function EditFieldTemplateDialog(props: EditFieldTemplateDialogProps) {
  const { t } = useTranslation();
  const {
    open,
    templateId,
    name,
    fieldJson,
    saving,
    onOpenChange,
    onChangeName,
    onChangeFieldJson,
    onConfirmSave,
  } = props;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[84vh] overflow-hidden sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('ai.editFieldTemplate.title')}</DialogTitle>
          <DialogDescription>
            {t('ai.editFieldTemplate.description', { id: templateId || '-' })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 overflow-y-auto pr-1">
          <div>
            <Label htmlFor="edit-template-name" className="text-xs text-slate-600">
              {t('ai.editFieldTemplate.templateName')}
            </Label>
            <Input
              id="edit-template-name"
              value={name}
              onChange={event => onChangeName(event.target.value)}
              className="mt-1 h-9"
              placeholder={t('ai.editFieldTemplate.templateNamePlaceholder')}
            />
          </div>
          <div>
            <Label htmlFor="edit-template-json" className="text-xs text-slate-600">
              {t('ai.editFieldTemplate.fieldJson')}
            </Label>
            <Textarea
              id="edit-template-json"
              value={fieldJson}
              onChange={event => onChangeFieldJson(event.target.value)}
              className="mt-1 min-h-[320px] font-mono text-xs"
              placeholder='[{"key":"...","label":"..."}]'
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>
            {t('common.dialog.cancel')}
          </Button>
          <Button type="button" disabled={saving} onClick={onConfirmSave}>
            {saving ? t('common.dialog.saving') : t('ai.editFieldTemplate.saveChanges')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
